import React, { type ComponentPropsWithoutRef, useState } from 'react';

import { Popup } from '@atlaskit/popup/popup';
import type { SearchConfluencePartial } from '@atlassian/dynamic-cards-search-client/graphql/confluence';
import { useCopyToClipboard } from '@atlassian/dynamic-cards-search-ui/person-card/actions';
import { PersonCard } from '@atlassian/dynamic-cards-search-ui/person-card/card';
import { ShortInlineSocialSignal } from '@atlassian/dynamic-cards-search-ui/person-card/trigger';

import { ACTION_SUBJECT_PREFIX } from '../../constants/analytics';
import { useAppContext } from '../app-context';

interface BreadcrumbSocialSignalProps {
	partial: Pick<SearchConfluencePartial, 'socialSignal'>;
}

export const BreadcrumbSocialSignal = ({
	partial,
}: BreadcrumbSocialSignalProps): React.JSX.Element | null => {
	const copyEmailToClipboard = useCopyToClipboard(partial.socialSignal?.user?.email ?? '');
	const [isOpen, setIsOpen] = useState(false);
	const { mapUserIdToProfileUrl } = useAppContext();

	const { socialSignal } = partial;
	if (
		!socialSignal ||
		!socialSignal.user ||
		!socialSignal.signal ||
		!isSupportedSocialSignal(socialSignal.signal)
	) {
		return null;
	}
	const { signal, timestamp, user } = socialSignal;

	const userProfileUrl = mapUserIdToProfileUrl(user.accountId);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			trigger={(triggerProps) => (
				<ShortInlineSocialSignal
					{...triggerProps}
					userName={user.name}
					socialSignalType={signal}
					avatarUrl={user.picture}
					onClick={() => {
						setIsOpen((open) => !open);
					}}
					testId="search-page-social-signal"
					componentName={`${ACTION_SUBJECT_PREFIX}SocialSignal`}
				/>
			)}
			content={() => (
				<PersonCard
					user={{
						name: user.name,
						profilePicUrl: user.picture,
						jobTitle: user.extendedProfile?.jobTitle,
						profileUrl: userProfileUrl,
					}}
					onCopyEmail={copyEmailToClipboard}
					signalDate={timestamp ? new Date(timestamp) : undefined}
					signalType={signal}
				/>
			)}
			shouldRenderToParent
		/>
	);
};

type SupportedSocialSignal = ComponentPropsWithoutRef<
	typeof ShortInlineSocialSignal
>['socialSignalType'];
const supportedSignals: SupportedSocialSignal[] = ['edit', 'share', 'comment'];
function isSupportedSocialSignal(signalFromApi: any): signalFromApi is SupportedSocialSignal {
	return supportedSignals.includes(signalFromApi);
}
